import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { type MouseEvent, type PropsWithChildren, type ReactNode } from 'react';
import ReactDOMServer from 'react-dom/server';
import { v4 as uuidv4 } from 'uuid';

import { type UUID } from '~/types/common';

import { closeBackdrop, openBackdrop } from '~/redux/backdropSlice';
import { useDispatch } from '~/redux/store';

import type InvoiceModel from '~/models/invoices/Invoice';

import { joinComponents } from '~/utils/array';

import { Tooltip } from '~/ui/atoms';

import BrowserUtils from './browserUtils';

type DeliveryNote = {
  readonly id: UUID;
  readonly number: string;
};

type FunctionalLinkProps = {
  readonly to: string;
  readonly message: string;
  readonly backdropMessage?: string;
  readonly backdropDuration?: number;
} & ComponentStyling;

type DeliveryNoteLinkProps = DeliveryNote & ComponentStyling;

type DeliveryNoteLinksProps = {
  deliveryNotes: DeliveryNote[];
} & ComponentStyling;

type InvoiceLinkProps = {
  readonly id: UUID | undefined;
  readonly number: string;
} & ComponentStyling;

type InvoiceLinksProps = {
  invoices: InvoiceModel[];
} & ComponentStyling;

export const MissingPermissionsTooltip = ({ children }: PropsWithChildren) => (
  <Tooltip
    title="Für diese Aktion fehlen dir die notwendigen Berechtigungen."
    width="xl"
    shouldWrapChildren
  >
    {children}
  </Tooltip>
);

//! this whole utility class needs to be refactored as it is being used somewhere before the Auth context is ready, so accessing navigation context is not possible. That's why we have to manually pass the nav links.
// TODO: Refactor this whole utility class
export const FunctionalLink = (props: FunctionalLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (location?.pathname === props.to) {
      return;
    }

    if (!props.backdropDuration) {
      navigate({ to: props.to });
      return;
    }

    const id = uuidv4();

    dispatch(
      openBackdrop({
        id,
        message: props.backdropMessage ?? '',
      }),
    );

    setTimeout(() => {
      navigate({ to: props.to });
      dispatch(closeBackdrop({ id }));
    }, props.backdropDuration ?? 0);
  };

  // setTimeout can't be used because browser would block window.open because of popup
  const onContextMenu = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    BrowserUtils.openNewTab(props.to);
  };

  return (
    <Link
      className={props.className}
      style={{
        textDecoration: 'none',
      }}
      to={props.to}
      onClick={handleClick}
      onContextMenu={onContextMenu}
    >
      {props.message}
    </Link>
  );
};

export const DeliveryNoteLink = (props: DeliveryNoteLinkProps) => {
  if (!props.id) {
    return `Lieferung ${props.number ?? ''}`;
  }

  return (
    <FunctionalLink
      className={props.className ?? 'text-primary500 font-bold'}
      message={`Lieferung ${props.number ?? ''}`}
      to={`/deliveries/${props.id}`}
    />
  );
};

export const DeliveryNoteLinks = (props: DeliveryNoteLinksProps) => {
  const displayLink = props.deliveryNotes.every(({ id }) => id);

  if (!displayLink) {
    return `Lieferung ${props.deliveryNotes.map((deliveryNote) => deliveryNote.number).join(', ')}`;
  }

  const deliveryNoteLinks = props.deliveryNotes.map((deliveryNote, index) =>
    index === 0 ? (
      <FunctionalLink
        key={deliveryNote.id}
        className={props.className ?? 'text-primary500 font-bold'}
        message={`Lieferung ${deliveryNote.number ?? ''}`}
        to={`/deliveries/${deliveryNote.id}`}
      />
    ) : (
      <FunctionalLink
        key={deliveryNote.id}
        className={props.className ?? 'text-primary500 font-bold'}
        message={deliveryNote.number ?? ''}
        to={`/deliveries/${deliveryNote.id}`}
      />
    ),
  );

  return joinComponents(deliveryNoteLinks);
};

export const InvoiceLink = (props: InvoiceLinkProps) => {
  if (!props.id) {
    return `Rechnung ${props.number ?? ''}`;
  }

  return (
    <FunctionalLink
      className={props.className ?? 'text-primary500 font-bold'}
      message={`Rechnung ${props.number ?? ''}`}
      to={`/invoices/incoming/${props.id}`}
    />
  );
};

export const InvoiceLinks = (props: InvoiceLinksProps) => {
  const displayLink = props.invoices.every(({ id }) => id);

  if (!displayLink) {
    return `Rechnung ${props.invoices.map(({ number }) => number).join(', ')}`;
  }

  const invoiceLinks = props.invoices.map((invoice, index) =>
    index === 0 ? (
      <FunctionalLink
        key={invoice.id}
        className={props.className ?? 'text-primary500 font-bold'}
        message={`Rechnung ${invoice.number ?? ''}`}
        to={`/invoices/incoming/${invoice.id}`}
      />
    ) : (
      <FunctionalLink
        key={invoice.id}
        className={props.className ?? 'text-primary500 font-bold'}
        message={invoice.number ?? ''}
        to={`/invoices/incoming/${invoice.id}`}
      />
    ),
  );

  return joinComponents(invoiceLinks);
};

class ComponentUtils {
  getStringFromComponent(component: ReactNode) {
    let string = ReactDOMServer.renderToString(component);

    string = string.replace('<!-- -->', '');

    return string;
  }

  // Finds out if flex box has wrapped by comparing if DOM elements with the same class name have a different top position.
  detectWrap(className: string) {
    const wrappedItems = [];
    let previousItem: Partial<DOMRect> = {};
    let currentItem: Partial<DOMRect> = {};

    const items = document.getElementsByClassName(className);

    for (const item of items) {
      currentItem = item.getBoundingClientRect();
      if (
        previousItem.top &&
        currentItem.top &&
        previousItem.top < currentItem.top
      ) {
        wrappedItems.push(item);
      }

      previousItem = currentItem;
    }

    return wrappedItems;
  }
}

const componentUtilsInstance = new ComponentUtils();

export default componentUtilsInstance;
